import type React from 'react';
import { useCallback } from 'react';
import { hexToVec3 } from '../../utils';
import type { FormField } from '../../form-types';
import type { BaseShaderConfig } from '../../base-config';
import { TextureShader } from '../../texture-shader';
import {
  ShaderBehavior,
  ShaderCategory,
  type ShaderMetadata,
} from '../../shader-types';
import { useShaderPreview } from '../../hooks/useShaderPreview';

// 1. SlimeballShader Model
export type SlimeballShaderConfig = BaseShaderConfig & {
  name: 'slimeball';
  duration: number;
  color: string;
  backgroundColor: string;
  morph: number;
  slime: number;
};
export class SlimeballShader extends TextureShader {
  name = 'slimeball' as const;
  width!: number;
  height!: number;
  referenceWidth!: number;
  referenceHeight!: number;
  color!: string;
  backgroundColor!: string;
  morph!: number;
  slime!: number;

  constructor(props: Partial<SlimeballShader> = {}) {
    super();

    // Set defaults
    this.name = 'slimeball';
    this.width = 512;
    this.height = 512;
    this.referenceWidth = 512;
    this.referenceHeight = 512;
    this.color = '#00ff00';
    this.backgroundColor = '#000000';
    this.morph = 0.5;
    this.slime = 0.5;

    // Apply provided props
    Object.assign(this, props);
  }

  generate(): string {
    return `
            precision mediump float;
            ${this.getTextureUniforms()}
            uniform vec3 u_color;
            uniform vec3 u_background_color;
            uniform float u_morph;
            uniform float u_slime;
            float random (in vec2 st) {
                return fract(sin(dot(st.xy,
                                     vec2(12.9898,78.233)))
                             * 43758.5453123);
            }
            float noise (in vec2 st) {
                vec2 i = floor(st);
                vec2 f = fract(st);
                // Four corners in 2D of a tile
                float a = random(i);
                float b = random(i + vec2(1.0, 0.0));
                float c = random(i + vec2(0.0, 1.0));
                float d = random(i + vec2(1.0, 1.0));
                vec2 u = f * f * (3.0 - 2.0 * f);
                return mix(a, b, u.x) +
                        (c - a)* u.y * (1.0 - u.x) +
                        (d - b) * u.y * u.x;
            }

            float fbm(vec2 p) {
                float value = 0.0;
                float amplitude = 0.5;
                for(int i = 0; i < 4; i++) {
                    value += amplitude * noise(p);
                    p *= 2.0;
                    amplitude *= 0.5;
                }
                return value;
            }

            float sd_box(vec2 p, vec2 s) {
                p = abs(p) - s;
                return max(p.x, p.y);
            }

            void main() {
                ${this.getTextureUVPreamble()}

                // If we're outside the reference bounds, show background
                if (isOutOfBounds) {
                    gl_FragColor = vec4(u_background_color, 1.0);
                    return;
                }

                // Convert to centered coordinates for the effect
                vec2 centered_uv = (uv * 2.0 - 1.0) * vec2(u_reference_resolution.x / u_reference_resolution.y, 1.0);
                float t = u_time;

                // Enhanced morphing with multiple noise layers
                float morph_val = (sin(t * 3.14159) + 1.0) / 2.0;

                // Add organic pulsing and breathing
                float pulse = sin(t * 6.28) * 0.1 + 1.0;
                float breath = sin(t * 2.0) * 0.05;

                // Multiple distortion layers for more organic feel
                vec2 distortion1 = vec2(fbm(centered_uv * 3.0 + t), fbm(centered_uv * 3.0 + t + 10.0)) * u_slime * 0.3;
                vec2 distortion2 = vec2(noise(centered_uv * 8.0 + t * 0.5), noise(centered_uv * 8.0 + t * 0.5 + 5.0)) * u_slime * 0.2;

                vec2 distorted_uv = centered_uv + distortion1 + distortion2;

                float d_circle = length(distorted_uv) * pulse;
                float d_box = sd_box(distorted_uv, vec2(0.5 + breath));
                float d = mix(d_circle, d_box, morph_val * u_morph);

                // Add surface tension effect
                float surface_tension = smoothstep(0.45, 0.55, d);

                // Enhanced edge with glow effect
                float edge_glow = 1.0 - smoothstep(0.5, 0.7, d);

                vec3 color = u_background_color;
                if (d < 0.5) {
                    // Core slime color with internal variations
                    float internal_pattern = fbm(centered_uv * 6.0 + t * 0.3) * 0.3 + 0.7;
                    color = u_color * internal_pattern;
                }

                // Add rim lighting effect
                color += u_color * edge_glow * 0.5;

                gl_FragColor = vec4(color, 1.0);
            }
        `;
  }

  static getFormConfig(): FormField[] {
    return [
      {
        type: 'range',
        name: 'morph',
        label: 'Morph Amount',
        description: 'How much the slime morphs and moves',
        min: 0.1,
        max: 3.0,
        step: 0.1,
        defaultValue: 0.5,
        colspan: 1,
      },
      {
        type: 'range',
        name: 'slime',
        label: 'Slime Thickness',
        description: 'Thickness and viscosity of the slime',
        min: 0.1,
        max: 2.5,
        step: 0.1,
        defaultValue: 0.5,
        colspan: 2, // Full width for this main property
      },
      {
        type: 'color',
        name: 'color',
        label: 'Slime Color',
        description: 'Main color of the slime',
        defaultValue: '#00ff00',
        colspan: 1,
      },
      {
        type: 'color',
        name: 'backgroundColor',
        label: 'Background Color',
        description: 'Background color behind the slime',
        defaultValue: '#000000',
        colspan: 1,
      },
    ];
  }

  static getMetadata(): ShaderMetadata {
    return {
      behavior: ShaderBehavior.TEXTURE,
      category: ShaderCategory.EFFECTS,
      description:
        'Morphing slime effect with customizable viscosity and movement',
      tags: ['slime', 'morph', 'organic', 'effect', 'viscous'],
    };
  }
}

// 3. SlimeballPreview Component
export const SlimeballPreview: React.FC<{ model: SlimeballShader }> = ({
  model,
}) => {
  const getUniformNames = useCallback(
    () => ['u_morph', 'u_slime', 'u_color', 'u_background_color'],
    []
  );

  const updateUniforms = useCallback(
    (
      gl: WebGLRenderingContext,
      locations: Record<string, WebGLUniformLocation | null>,
      model: SlimeballShader
    ) => {
      // Update shader-specific uniforms
      gl.uniform1f(locations.u_morph, model.morph);
      gl.uniform1f(locations.u_slime, model.slime);

      // Update color uniforms
      const colorVec3 = hexToVec3(model.color);
      const backgroundColorVec3 = hexToVec3(model.backgroundColor);

      gl.uniform3fv(locations.u_color, colorVec3);
      gl.uniform3fv(locations.u_background_color, backgroundColorVec3);
    },
    []
  );

  const canvasRef = useShaderPreview(model, getUniformNames, updateUniforms);

  return <canvas ref={canvasRef} width={model.width} height={model.height} />;
};
